import { useMemo } from 'react'
import { ManifestSpec } from '@signifyd/cypress'
import { useDashboardParams } from 'core/queryParams/useDashboardParams'
import { GetRunsResponse } from 'lib/queries/useGetRuns/runs.types'
import { useGetRuns } from 'lib/queries/useGetRuns/useGetRuns'

export type SpecForRun = ManifestSpec & {
  runId: string
}

interface UseGetSpecsForRunResult {
  specs: Array<SpecForRun>
  isLoading: boolean
}

/**
 * Flattens the per-project run list into one history row per run that ran the
 * given spec file. Each row carries the run's datetime (a valid ISO string),
 * not the spec's own datetime, which the reporter writes colon-mangled for
 * filename safety and cannot be parsed by moment/Date.
 */
export const collectSpecsForRun = (
  allRuns: GetRunsResponse | undefined,
  specFile?: string
): Array<SpecForRun> => {
  if (!allRuns?.length || !specFile) {
    return []
  }

  return allRuns.flatMap((run) => {
    const spec = run.specs.find((spec) => spec.specFileName === specFile)

    return spec ? [{ runId: run.runId, ...spec, datetime: run.datetime }] : []
  })
}

export const useGetSpecsForRun = (
  specFile?: string,
  projectName?: string
): UseGetSpecsForRunResult => {
  const {
    query: { suite },
  } = useDashboardParams()

  const { data: allRuns, isLoading } = useGetRuns({
    project: projectName,
    suite,
  })

  const specs = useMemo(
    () => collectSpecsForRun(allRuns, specFile),
    [allRuns, specFile]
  )

  return { specs, isLoading }
}
